import * as React from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { isEqual, isNil, sortBy, xor } from 'lodash';

import { navigate } from 'libs/router';

import ActionButton from 'components/buttons/action-button/ActionButton';
import InfluencerSelector from 'components/influencer-selector/InfluencerSelector';
import Spinner from 'components/spinner/Spinner';
import Pagination from 'components/pagination/Pagination';

import DeliverableCheckboxFilters, { SUPPORTED_TYPE_OPTIONS } from 'modules/admin/components/deliverables/checkbox-filters/DeliverableCheckboxFilters';
import DeliverablesMetricsTable from 'modules/admin/components/deliverables/deliverables-metrics-table/DeliverablesMetricsTable';
import MetricsTempFBBanner from '../metrics-temp-fb/banner/MetricsTempFBBanner';

import { fetchSubcampaigns, resetSubcampaigns } from '../../../reducers/campaignSubcampaigns';
import { fetchDeliverables, resetDeliverables } from './reducers/campaign-report-metrics';

import { isDefined } from 'utils/helper/common';
import { __t, applyTranslations } from 'utils/language';
import { getLocationUrl, getLocationWithExtendedQuery } from 'utils/client';
import { SUBCAMPAIGN_STATE_ACCEPTED, isDraft } from 'utils/subcampaign';
import { updateQueryParams } from 'utils/location';

import './campaign-report-metrics.scss';

import type { CampaignReportMetricsDeliverables } from './types/CampaignReportMetrics.type';
import type {
  AdminDeliverablesQuery,
  AdminDeliverablesSelectionQuery,
  CampaignOverview,
  DeliverableType,
  InfluencerSelector as InfluencerSelectorAlias,
  LocationQuery,
  LocationWindow,
  Pagination as PaginationType,
  Subcampaign,
  TextEntry
} from 'types';

import type { SyntheticEvent } from 'react';

applyTranslations(require('./campaign-report-metrics.i18n.json'));

type OwnProps = {
  campaignId: number,
  location: LocationWindow,
  isFullscreen?: boolean
};

type StateProps = {
  deliverables: CampaignReportMetricsDeliverables,
  pagination: PaginationType,
  subcampaignsList: Array<Subcampaign>,
  subcampaignsIsLoading: boolean,
  campaignDeliverableTypes?: Array<DeliverableType>,
  overview: CampaignOverview
};

type DispatchProps = {
  dispatchFetchSubcampaigns: (a: number) => Promise<void>,
  dispatchFetchDeliverables: (b: number, a?: LocationQuery | null) => Promise<void>,
  dispatchResetSubcampaigns: () => Promise<void>,
  dispatchResetDeliverables: () => Promise<void>
};

type Props = OwnProps & StateProps & DispatchProps;

const msgKey = 'campaign_report_metrics';

class CampaignReportMetrics extends React.PureComponent<Props> {
  static defaultProps = {
    isFullscreen: true
  };

  componentDidMount() {
    const { campaignId, dispatchFetchSubcampaigns } = this.props;

    dispatchFetchSubcampaigns(campaignId);
    this.fetchDeliverables();
    this.setQueryTypes();
  }

  componentDidUpdate(prevProps: Props) {
    const { location, campaignDeliverableTypes } = this.props;

    if (!isEqual(campaignDeliverableTypes, prevProps.campaignDeliverableTypes)) {
      this.setQueryTypes();
    }

    if (!isEqual(location, prevProps.location)) {
      this.fetchDeliverables();

      window.scrollTo(0, 0);
    }
  }

  componentWillUnmount() {
    const { dispatchResetSubcampaigns, dispatchResetDeliverables } = this.props;

    dispatchResetSubcampaigns();
    dispatchResetDeliverables();
  }

  setQueryTypes = () => {
    const { location, campaignDeliverableTypes } = this.props;
    const selection = this.getSelectionFromQuery(location.query);
    if (selection.type.length === 0) {
      // in case of empty preselected types select all available
      this.handleNavigate({
        type: (campaignDeliverableTypes ?? []).join(',')
      });
    }
  };

  fetchDeliverables = () => {
    const { campaignId, location, dispatchFetchDeliverables } = this.props;

    dispatchFetchDeliverables(campaignId, location.query);
  };

  getSelectionFromQuery = (query?: AdminDeliverablesQuery | null): AdminDeliverablesSelectionQuery => {
    const status = query?.status?.split(',') || [];
    const type = query?.type?.split(',') || [];
    const mismatchedAnalytics = query?.mismatchedAnalytics === 'true' || false;
    const influencerId = query?.influencerId || '';

    return {
      status: status.filter(isDefined),
      type: type.filter(isDefined),
      influencerId,
      mismatchedAnalytics
    };
  };

  getInfluencersForSelector(): Array<InfluencerSelectorAlias> {
    const { subcampaignsList } = this.props;

    const updatedSubcampaignsList = sortBy(subcampaignsList, ['updatedAt', 'creatorProfile.name'])
      .filter(s => !isDraft(s) && isNil(s.revokedBy));

    return updatedSubcampaignsList.map(subcampaign => {
      const creatorProfile = subcampaign?.creatorProfile || {};

      return {
        id: creatorProfile.id,
        name: creatorProfile.name,
        offerId: subcampaign?.offer?.id,
        subcampaignState: subcampaign?.state,
        profileImage: creatorProfile.profileImage,
        hasActiveFbConnection: creatorProfile.hasActiveFbConnection
      };
    });
  }

  handleNavigate(queryParams: { [name: string]: string | number | boolean }): void {
    const { location } = this.props;

    navigate.to(getLocationUrl(getLocationWithExtendedQuery(location, {
      page: 1,
      ...queryParams
    })));
  }

  handlePageChange = (page: number): void => {
    const { location } = this.props;

    updateQueryParams(location, { page });
  };

  handleInfluencerSelect = (influencerProfileId?: number | null): void => {
    this.handleNavigate({
      influencerId: influencerProfileId || ''
    });
  };

  handleFilterChange = (event: SyntheticEvent<TextEntry>): void => {
    const target = event.currentTarget;
    const name = target.name;
    const value = target.value;

    if (!name) {
      return;
    }

    const { location } = this.props;
    const selection = this.getSelectionFromQuery(location.query);

    this.handleNavigate({
      [name]: xor(selection[name], [value]).join(',')
    });
  };

  handleBooleanFilterChange = (event: SyntheticEvent<TextEntry>): void => {
    const name = event.currentTarget.name;

    if (!name) {
      return;
    }

    const { location } = this.props;
    const selection = this.getSelectionFromQuery(location.query);

    this.handleNavigate({
      [name]: !selection[name]
    });
  };

  renderSpinner(): React.ReactNode {
    return (
      <div className="campaign-report-metrics__spinner">
        <Spinner
          position="inline"
          size="small"
          color="light"
        />
      </div>
    );
  }

  renderTitleSection(): React.ReactNode {
    const { campaignId } = this.props;

    return (
      <div className="campaign-report-metrics__title-section">
        <ActionButton
          link={`/campaigns/${campaignId}/report`}
        >
          {__t(`${msgKey}.buttons.return_to_reporting`)}
        </ActionButton>
        <div className="campaign-report-metrics__title-value">
          {__t(`${msgKey}.labels.edit_content_metrics`)}
        </div>
      </div>
    );
  }

  renderInfluencerSelector(
    influencerProfiles: Array<InfluencerSelectorAlias>,
    selectedInfluencerProfileId?: number | null
  ): React.ReactNode {
    const resetButton = {
      label: __t(`${msgKey}.buttons.all_creators`),
      icon: 'users-connected-white-bg-custom-fg'
    };

    return (
      <div className="campaign-report-metrics__influencer-selector">
        <InfluencerSelector
          selectedInfluencerProfileId={selectedInfluencerProfileId}
          influencerProfiles={influencerProfiles}
          shouldSort={false}
          resetButton={resetButton}
          onSelect={this.handleInfluencerSelect}
          showSearch
        />
      </div>
    );
  }

  renderFilters(): React.ReactNode {
    const { location, campaignDeliverableTypes } = this.props;

    return (
      <div className="campaign-report-metrics__filters">
        <DeliverableCheckboxFilters
          location={location}
          getSelectionFromQuery={this.getSelectionFromQuery}
          onFilterChange={this.handleFilterChange}
          onBooleanFilterChange={this.handleBooleanFilterChange}
          campaignDeliverableTypes={campaignDeliverableTypes || []}
        />
      </div>
    );
  }

  renderFBDataBanner = (): React.ReactNode => {
    const influencers = this.getInfluencersForSelector();
    const shouldShowBanner = influencers.some(i => i.hasActiveFbConnection);

    if (!shouldShowBanner) {
      return null;
    }

    return (
      <div className="campaign-report-metrics__fb-banner">
        <MetricsTempFBBanner />
      </div>
    );
  };

  renderPostsTable(selectedInfluencer?: InfluencerSelectorAlias | null): React.ReactNode {
    const { deliverables, location, pagination, isFullscreen, campaignId, overview } = this.props;
    const items = deliverables.items || [];

    return (
      <div className="campaign-report-metrics__posts-table">
        <DeliverablesMetricsTable
          isCampaignCA2={overview?.contentApprovalV2}
          location={location}
          campaignId={campaignId}
          // @ts-ignore
          selectedInfluencer={selectedInfluencer?.subcampaignState === SUBCAMPAIGN_STATE_ACCEPTED ? selectedInfluencer : null}
          deliverablesMetrics={items}
          onUpdate={this.fetchDeliverables}
          isFullscreen={isFullscreen}
          deliverableUploadedEventLabel="content metrics of reporting v2"
          renderFBDataBanner={this.renderFBDataBanner}
        />
        <Pagination
          location={location}
          meta={pagination}
          links={pagination.links}
          onClick={this.handlePageChange}
        />
      </div>
    );
  }

  renderPosts(selectedInfluencer?: InfluencerSelectorAlias | null): React.ReactNode {
    const { deliverables } = this.props;

    return (
      <div className="campaign-report-metrics__posts">
        {
          deliverables.isLoading
            ? this.renderSpinner()
            : this.renderPostsTable(selectedInfluencer)
        }
      </div>
    );
  }

  renderMetrics(): React.ReactNode {
    const { location, isFullscreen } = this.props;
    const selection = this.getSelectionFromQuery(location.query);
    const selectedInfluencerProfileId = Number(selection.influencerId);

    const influencers = this.getInfluencersForSelector();
    const selectedInfluencer = influencers.find(influencer => influencer.id === selectedInfluencerProfileId);

    return (
      <React.Fragment>
        {isFullscreen && this.renderTitleSection()}
        {this.renderInfluencerSelector(influencers, selectedInfluencerProfileId)}
        {this.renderFilters()}
        {this.renderPosts(selectedInfluencer)}
      </React.Fragment>
    );
  }

  render(): React.ReactNode {
    const { subcampaignsIsLoading, isFullscreen } = this.props;

    const containerClasses = classnames('campaign-report-metrics', {
      'campaign-report-metrics--full-screen': Boolean(isFullscreen)
    });

    return (
      <div className={containerClasses}>
        {
          subcampaignsIsLoading
            ? this.renderSpinner()
            : this.renderMetrics()
        }
      </div>
    );
  }
}

const mapStateToProps = ({ campaignSubcampaigns, campaignReportingMetrics, reportingMetaInfo, campaignOverview }): StateProps => ({
  subcampaignsIsLoading: campaignSubcampaigns.isLoading,
  subcampaignsList: campaignSubcampaigns.list,
  deliverables: campaignReportingMetrics.deliverables,
  pagination: campaignReportingMetrics.pagination,
  campaignDeliverableTypes: SUPPORTED_TYPE_OPTIONS
    .filter(option => reportingMetaInfo?.metaInfo?.campaignDeliverableTypes?.includes(option)),
  overview: campaignOverview.overview
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchSubcampaigns: (campaignId: number) => (
    dispatch(fetchSubcampaigns(campaignId, { showLoadingComponent: false, acceptedOnly: true }))
  ),

  dispatchFetchDeliverables: (campaignId: number, query?: LocationQuery | null) => (
    dispatch(fetchDeliverables(campaignId, query))
  ),

  dispatchResetSubcampaigns: () => dispatch(resetSubcampaigns()),
  dispatchResetDeliverables: () => dispatch(resetDeliverables())
});

export default connect(mapStateToProps, mapDispatchToProps)(CampaignReportMetrics);
